import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../api';
import { WinnerTypeChip } from './WinnerAssetsOverview';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import WinnerAssetExportButton from '../components/WinnerAssetExportButton';

function formatBytes(bytes) {
    if (!Number.isFinite(bytes) || bytes <= 0) return '0 bytes';
    if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    if (bytes >= 1024) return `${Math.round(bytes / 1024)} KB`;
    return `${bytes} bytes`;
}

function isHeicOrHeif(asset) {
    return /\.(heic|heif)$/i.test(String(asset?.name || ''))
        || /^image\/(heic|heif)$/i.test(String(asset?.contentType || ''));
}

export default function WinnerAssetSchoolDetail() {
    const { year, schoolId } = useParams();
    const navigate = useNavigate();
    const [data, setData] = React.useState(null);
    const [error, setError] = React.useState('');
    const [previewIndex, setPreviewIndex] = React.useState(-1);

    React.useEffect(() => {
        api.get(`/admin/api/winner-assets/${year}/schools/${schoolId}`)
            .then((response) => setData(response.data))
            .catch((requestError) => setError(requestError?.response?.data?.message || 'Unable to load winner assets.'));
    }, [year, schoolId]);

    const previewImages = React.useMemo(() => (data?.categories || []).flatMap((category) => category.assets || [])
        .filter((asset) => asset.previewable), [data]);
    const previewFile = previewIndex >= 0 ? previewImages[previewIndex] : null;

    React.useEffect(() => {
        if (!previewFile) return undefined;
        const keydown = (event) => {
            if (event.key === 'Escape') setPreviewIndex(-1);
            if (event.key === 'ArrowLeft') setPreviewIndex((current) => Math.max(0, current - 1));
            if (event.key === 'ArrowRight') setPreviewIndex((current) => Math.min(previewImages.length - 1, current + 1));
        };
        window.addEventListener('keydown', keydown);
        return () => window.removeEventListener('keydown', keydown);
    }, [previewFile, previewImages.length]);

    if (error) return <Alert severity="error" sx={{ m: 4 }}>{error}</Alert>;
    if (!data) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;

    return (
        <Box sx={{ width: '92%', mx: 'auto', py: 4 }}>
            <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(`/winner-assets/${year}`)} sx={{ mb: 2 }}>Back to Winner Assets</Button>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', mb: 3 }}>
                <Typography variant="h4" component="h1">{data.schoolName} — {data.year} Winner Assets</Typography>
                <WinnerTypeChip label={data.winnerTypeLabel} />
                <Box sx={{ ml: { md: 'auto' } }}><WinnerAssetExportButton year={data.year} schoolId={data.schoolId} label="Download All Assets" /></Box>
            </Box>

            <Box sx={{ display: 'grid', gap: 3 }}>
                {(data.categories || []).map((category) => {
                    const previews = (category.assets || []).filter((asset) => asset.previewable);
                    const files = (category.assets || []).filter((asset) => !asset.previewable);
                    return (
                        <Paper key={category.key} variant="outlined" sx={{ p: 3, borderRadius: 1 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
                                <Box><Typography variant="h6" component="h2" sx={{ fontWeight: 700 }}>{category.label}</Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{category.assets?.length || 0} / {category.maxFiles} files</Typography></Box>
                                {(category.assets || []).length > 0 && <WinnerAssetExportButton year={data.year} schoolId={data.schoolId} categoryKey={category.key} label={`Download ${category.label}`} />}
                            </Box>
                            {previews.length > 0 && (
                                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 2, mb: files.length ? 2 : 0 }}>
                                    {previews.map((asset) => (
                                        <Box key={asset.assetId} sx={{ border: '1px solid', borderColor: 'divider', minWidth: 0, overflow: 'hidden' }}>
                                            <Box component="button" type="button" aria-label={`Preview ${asset.name}`}
                                                onClick={() => setPreviewIndex(previewImages.findIndex((item) => item.assetId === asset.assetId))}
                                                sx={{ display: 'block', width: '100%', border: 0, p: 0, bgcolor: 'transparent', cursor: 'pointer' }}>
                                                <Box component="img" src={asset.url} alt={asset.name} sx={{ width: '100%', aspectRatio: '4 / 3', objectFit: 'cover', display: 'block' }} />
                                            </Box>
                                            <AssetRow
                                                asset={asset}
                                                compact
                                                onPreview={() => setPreviewIndex(previewImages.findIndex((item) => item.assetId === asset.assetId))}
                                            />
                                        </Box>
                                    ))}
                                </Box>
                            )}
                            {files.length > 0 && <Box sx={{ display: 'grid', gap: 1 }}>{files.map((asset) => <AssetRow key={asset.assetId} asset={asset} />)}</Box>}
                            {(category.assets || []).length === 0 && <Typography color="text.secondary">No files uploaded.</Typography>}
                        </Paper>
                    );
                })}
            </Box>

            <Dialog open={Boolean(previewFile)} onClose={() => setPreviewIndex(-1)} maxWidth="xl" fullWidth>
                <DialogTitle>{previewFile ? `${previewFile.name} (${previewIndex + 1} of ${previewImages.length})` : 'Image preview'}</DialogTitle>
                <DialogContent sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 2, position: 'relative' }}>
                    {previewFile && <>
                        <IconButton aria-label="Previous image" disabled={previewIndex <= 0} onClick={() => setPreviewIndex((current) => Math.max(0, current - 1))}
                            sx={{ position: 'absolute', left: 12, bgcolor: 'rgba(255,255,255,.9)' }}><ChevronLeftIcon /></IconButton>
                        <Box component="img" src={previewFile.url} alt={`Preview of ${previewFile.name}`} sx={{ maxWidth: '90vw', maxHeight: '80vh', objectFit: 'contain' }} />
                        <IconButton aria-label="Next image" disabled={previewIndex >= previewImages.length - 1} onClick={() => setPreviewIndex((current) => Math.min(previewImages.length - 1, current + 1))}
                            sx={{ position: 'absolute', right: 12, bgcolor: 'rgba(255,255,255,.9)' }}><ChevronRightIcon /></IconButton>
                    </>}
                </DialogContent>
                <DialogActions>
                    {previewFile && <Button component="a" href={previewFile.url} target="_blank" rel="noopener noreferrer">Open in new tab</Button>}
                    <Button onClick={() => setPreviewIndex(-1)}>Close</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

function AssetRow({ asset, compact = false, onPreview }) {
    const { year, schoolId } = useParams();
    const download = async () => {
        try {
            const response = await api.get(`/admin/api/winner-assets/${year}/schools/${schoolId}/categories/${asset.categoryKey}/download?assetId=${encodeURIComponent(asset.assetId)}`);
            if (response.data?.url) window.location.assign(response.data.url);
        } catch (error) {
            // Keep the inline/open link available if an attachment URL cannot be prepared.
        }
    };
    if (compact) {
        return (
            <Box sx={{ p: 1.25, minWidth: 0 }}>
                <Typography
                    component="button"
                    type="button"
                    onClick={onPreview}
                    aria-label={`Preview ${asset.name} from filename`}
                    title={asset.name}
                    sx={{ display: 'block', width: '100%', p: 0, border: 0, bgcolor: 'transparent', color: 'primary.main', textAlign: 'left', cursor: 'pointer', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                >
                    {asset.name}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block">{formatBytes(asset.size)}</Typography>
                <Box data-testid={`admin-preview-card-actions-${asset.assetId}`} sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.5 }}>
                    <Button size="small" onClick={download}>Download</Button>
                </Box>
            </Box>
        );
    }
    return (
        <Box sx={{ p: 1.25, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, border: '1px solid', borderColor: 'divider' }}>
            <Box sx={{ minWidth: 0 }}>
                {isHeicOrHeif(asset) ? (
                    <Typography sx={{ overflowWrap: 'anywhere' }}>{asset.name}</Typography>
                ) : (
                    <Typography component="a" href={asset.url} target="_blank" rel="noopener noreferrer" sx={{ overflowWrap: 'anywhere' }}>{asset.name}</Typography>
                )}
                <Typography variant="caption" color="text.secondary" display="block">{formatBytes(asset.size)}</Typography>
            </Box>
            <Button size="small" onClick={download}>Download</Button>
        </Box>
    );
}